import { useCallback, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { useDidUpdateEffect } from '@namespace/helpers';
import { useParsePin } from '../useParsePin';

const STATES = {
  IDLE: 'IDLE',
  VALIDATING: 'VALIDATING',
  PARSING: 'PARSING'
};

export const usePinParsingFlow = ({ name, onStateChange }) => {
  const parsingProcessState = useRef(STATES.IDLE);
  const lastValidatedValue = useRef(null);
  const delayedExecutionFnRef = useRef();

  const parser = useParsePin();

  const transition = useCallback(
    (to, data) => {
      parsingProcessState.current = to;

      onStateChange?.(parsingProcessState.current, data);
    },
    [onStateChange]
  );

  const init = useCallback(
    (fn) => {
      delayedExecutionFnRef.current = fn;

      transition(STATES.VALIDATING);
    },
    [transition]
  );

  const complete = useCallback(
    (data) => {
      transition(STATES.IDLE, data);

      delayedExecutionFnRef.current(data);
      delayedExecutionFnRef.current = null;
    },
    [transition]
  );

  const parse = useCallback(
    async (value, stateData) => {
      transition(STATES.PARSING, stateData);

      const data = await parser.run(value);

      complete({ parsed: data });
    },
    [complete, parser, transition]
  );

  const {
    getValues,
    formState: { errors, isValidating }
  } = useFormContext();

  useDidUpdateEffect(() => {
    const value = getValues()[name];
    const isValueChanged = value !== lastValidatedValue.current;
    const isValidatingState = parsingProcessState.current === STATES.VALIDATING;
    const isPinValidated = isValidatingState && isValueChanged && !isValidating;
    const isPinValidationPassed = isPinValidated && !errors[name];

    if (isPinValidated) {
      lastValidatedValue.current = value;
    } else {
      if (isValidatingState && !isValidating) {
        complete();
      }

      return;
    }

    const stateData = { isValidationPassed: isPinValidationPassed };

    if (isPinValidationPassed) {
      parse(value, stateData);
    } else {
      complete(stateData);
    }
  }, [isValidating]);

  const handleStart = useCallback(
    (cb) => {
      init(cb);
    },
    [init]
  );

  return { start: handleStart };
};
